import { useEffect, useRef } from 'preact/hooks';
import { playSound } from '../../audio/sounds.js';
import {
  TETRAX_H,
  TETRAX_HIDDEN,
  TETRAX_W,
  dropY,
  hardDrop,
  hold,
  move,
  pieceCells,
  rotate,
  softDrop,
  tick,
  type TetraxEvents,
  type TetraxState,
} from '../../../shared/games/tetrax/index.js';

// Tetrax (user request 2026-10-03): the board on a canvas and the keyboard controls. Computer only
// (decision 2026-10-03: no phone version), so there are no touch controls.

/** Colours of the pieces I J L O S T Z and the garbage. */
export const TETRAX_COLORS = ['', '#22d3ee', '#3b82f6', '#f97316', '#facc15', '#22c55e', '#a855f7', '#ef4444', '#6b7280'];
const BG = '#0a0f1e';
const GRID = 'rgba(255, 255, 255, 0.05)';
/** The first repeat of a held arrow after this, then every ARR_MS. */
const DAS_MS = 160;
const ARR_MS = 45;
const SOFT_MS = 35;
/** A long pause (another tab) is not caught up at once. */
const MAX_FRAME_MS = 100;

function block(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string, alpha = 1) {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.fillRect(x, y, size, size);
  const edge = Math.max(2, Math.round(size / 8));
  ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
  ctx.fillRect(x, y, size, edge);
  ctx.fillRect(x, y, edge, size);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.fillRect(x, y + size - edge, size, edge);
  ctx.fillRect(x + size - edge, y, edge, size);
  ctx.globalAlpha = 1;
}

/** Sets the canvas size for the screen's pixel density; returns the context in CSS pixels. */
function prepare(canvas: HTMLCanvasElement, w: number, h: number): CanvasRenderingContext2D | null {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }
  const ctx = canvas.getContext('2d');
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

/** Draws the visible board, the ghost and the falling piece. */
export function drawField(canvas: HTMLCanvasElement, s: TetraxState, cell: number, opts: { ghost?: boolean; dim?: boolean } = {}): void {
  const ctx = prepare(canvas, TETRAX_W * cell, TETRAX_H * cell);
  if (!ctx) return;
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, TETRAX_W * cell, TETRAX_H * cell);
  ctx.strokeStyle = GRID;
  ctx.lineWidth = 1;
  for (let x = 1; x < TETRAX_W; x++) {
    ctx.beginPath();
    ctx.moveTo(x * cell + 0.5, 0);
    ctx.lineTo(x * cell + 0.5, TETRAX_H * cell);
    ctx.stroke();
  }
  for (let y = 1; y < TETRAX_H; y++) {
    ctx.beginPath();
    ctx.moveTo(0, y * cell + 0.5);
    ctx.lineTo(TETRAX_W * cell, y * cell + 0.5);
    ctx.stroke();
  }
  const alpha = opts.dim ? 0.45 : 1;
  for (let y = TETRAX_HIDDEN; y < TETRAX_H + TETRAX_HIDDEN; y++) {
    for (let x = 0; x < TETRAX_W; x++) {
      const c = s.board[y * TETRAX_W + x]!;
      if (c) block(ctx, x * cell, (y - TETRAX_HIDDEN) * cell, cell, TETRAX_COLORS[c]!, alpha);
    }
  }
  const p = s.piece;
  if (!p || s.over) return;
  const color = TETRAX_COLORS[p.type]!;
  if (opts.ghost !== false && !opts.dim) {
    const gy = dropY(s);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 2;
    for (const [x, y] of pieceCells({ ...p, y: gy })) {
      if (y >= TETRAX_HIDDEN) ctx.strokeRect(x * cell + 2, (y - TETRAX_HIDDEN) * cell + 2, cell - 4, cell - 4);
    }
    ctx.globalAlpha = 1;
  }
  for (const [x, y] of pieceCells(p)) if (y >= TETRAX_HIDDEN) block(ctx, x * cell, (y - TETRAX_HIDDEN) * cell, cell, color, alpha);
}

/** A board on a canvas, redrawn when `version` changes (the opponent's board, the end position). */
export function TetraxField(props: { state: TetraxState; cell: number; version: number; ghost?: boolean; dim?: boolean; label: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (canvas.current) drawField(canvas.current, props.state, props.cell, { ghost: props.ghost, dim: props.dim });
  }, [props.state, props.version, props.cell, props.dim]);
  return <canvas ref={canvas} class="tetrax-canvas" role="img" aria-label={props.label} />;
}

/** One piece in a small box (the hold and the next pieces). */
export function PieceBox(props: { type: number; cell?: number; dim?: boolean }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const cell = props.cell ?? 18;
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const ctx = prepare(el, cell * 4, cell * 2.5);
    if (!ctx) return;
    ctx.clearRect(0, 0, cell * 4, cell * 2.5);
    if (!props.type) return;
    const cells = pieceCells({ type: props.type, rot: 0, x: 0, y: 0 });
    const xs = cells.map(([x]) => x);
    const ys = cells.map(([, y]) => y);
    const w = Math.max(...xs) - Math.min(...xs) + 1;
    const h = Math.max(...ys) - Math.min(...ys) + 1;
    const ox = (4 - w) / 2 - Math.min(...xs);
    const oy = (2.5 - h) / 2 - Math.min(...ys);
    for (const [x, y] of cells) block(ctx, (x + ox) * cell, (y + oy) * cell, cell, TETRAX_COLORS[props.type]!, props.dim ? 0.4 : 1);
  }, [props.type, cell, props.dim]);
  return <canvas ref={canvas} class="tetrax-piece" aria-hidden="true" />;
}

/** Sounds of what happened. */
export function eventSounds(e: TetraxEvents): void {
  if (e.over) playSound('tetraxOver');
  else if (e.cleared >= 4) playSound('tetraxQuad');
  else if (e.cleared > 0) playSound('tetraxClear');
  else if (e.locked) playSound('tetraxLock');
  if (e.garbage > 0 && !e.over) playSound('tetraxGarbage');
}

/**
 * The game loop and the keyboard while `active`: the piece falls with time, held arrows repeat.
 * `frame` is called after every animation frame (drawing), `onEvents` after a lock and
 * `onChange` when the piece or the board changed (the duel sends its board then).
 */
export function useTetraxLoop(opts: {
  state: { current: TetraxState };
  active: boolean;
  frame: () => void;
  onEvents?: (e: TetraxEvents) => void;
  onChange?: () => void;
  onPause?: () => void;
}): void {
  const latest = useRef(opts);
  latest.current = opts;
  useEffect(() => {
    if (!opts.active) return;
    const held = { left: 0, right: 0, down: 0 };
    const next = { left: 0, right: 0, down: 0 };
    const s = () => latest.current.state.current;
    const changed = () => latest.current.onChange?.();
    const events = (e: TetraxEvents) => {
      if (!e.locked && !e.over) return;
      eventSounds(e);
      latest.current.onEvents?.(e);
      changed();
    };
    const step = (dir: 'left' | 'right') => move(s(), dir === 'left' ? -1 : 1) && changed();

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const now = performance.now();
      const down = e.type === 'keydown';
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const arrow = key === 'ArrowLeft' ? 'left' : key === 'ArrowRight' ? 'right' : key === 'ArrowDown' ? 'down' : null;
      if (arrow) {
        e.preventDefault();
        if (!down) {
          held[arrow] = 0;
          return;
        }
        if (e.repeat || held[arrow]) return;
        held[arrow] = now;
        if (arrow === 'down') {
          if (softDrop(s())) changed();
          next.down = now + SOFT_MS;
        } else {
          // The newer direction wins while both are held.
          held[arrow === 'left' ? 'right' : 'left'] = 0;
          step(arrow);
          next[arrow] = now + DAS_MS;
        }
        return;
      }
      if (!down || e.repeat) return;
      if (key === 'ArrowUp' || key === 'x') {
        e.preventDefault();
        if (rotate(s(), 1)) {
          playSound('tetraxRotate');
          changed();
        }
      } else if (key === 'z' || key === 'Control') {
        e.preventDefault();
        if (rotate(s(), -1)) {
          playSound('tetraxRotate');
          changed();
        }
      } else if (key === ' ') {
        e.preventDefault();
        events(hardDrop(s()));
      } else if (key === 'c' || key === 'Shift') {
        e.preventDefault();
        if (hold(s())) changed();
      } else if (key === 'p' || key === 'Escape') {
        latest.current.onPause?.();
      }
    };
    // Keys let go while the window was elsewhere would stay held.
    const release = () => {
      held.left = held.right = held.down = 0;
    };

    let last = performance.now();
    let raf = requestAnimationFrame(function loop(now) {
      const dt = Math.min(MAX_FRAME_MS, now - last);
      last = now;
      for (const dir of ['left', 'right'] as const) {
        while (held[dir] && now >= next[dir]) {
          step(dir);
          next[dir] += ARR_MS;
        }
      }
      while (held.down && now >= next.down) {
        if (softDrop(s())) changed();
        next.down += SOFT_MS;
      }
      const before = s().piece?.y;
      events(tick(s(), dt));
      if (s().piece?.y !== before) changed();
      latest.current.frame();
      if (!s().over) raf = requestAnimationFrame(loop);
    });
    addEventListener('keydown', onKey);
    addEventListener('keyup', onKey);
    addEventListener('blur', release);
    return () => {
      cancelAnimationFrame(raf);
      removeEventListener('keydown', onKey);
      removeEventListener('keyup', onKey);
      removeEventListener('blur', release);
    };
  }, [opts.active]);
}
